import { useEffect, useState } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { getPortfolioValueHistory } from "@/lib/api";

const RANGES = [{ label: "30D", days: 30 }, { label: "90D", days: 90 }, { label: "All", days: 3650 }];
const usd = (v) => `$${Math.round(v || 0).toLocaleString()}`;

const TrendTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="bg-bt-surface border border-bt-border-focus px-3 py-2 font-mono text-[11px]" data-testid="value-trend-tooltip">
      <p className="text-bt-text-tertiary mb-1">{label}</p>
      <p className="text-bt-text-primary">Total {usd(p.total)}</p>
      {p.portfolio != null && <p className="text-bt-text-secondary">My Portfolio {usd(p.portfolio)}</p>}
      {p.woodrow != null && <p className="text-bt-text-secondary">Woodrow {usd(p.woodrow)}</p>}
    </div>
  );
};

const withLive = (points, live) => {
  if (!live) return points;
  const today = new Date().toISOString().slice(0, 10);
  const pt = { date: today, woodrow: live.woodrow, portfolio: live.portfolio, total: (live.woodrow || 0) + (live.portfolio || 0) };
  return [...points.filter(p => p.date !== today), pt];
};

const seriesChange = (points, key) => {
  const s = points.filter(p => p[key] != null);
  return s.length > 1 ? s[s.length - 1][key] - s[0][key] : 0;
};

export const ValueTrendChart = ({ live }) => {
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);
  const [autoFell, setAutoFell] = useState(false);

  useEffect(() => {
    getPortfolioValueHistory(days).then(r => {
      if (!autoFell && days !== 3650 && (r.data?.points?.length || 0) < 2) {
        setAutoFell(true);
        setDays(3650);
        return;
      }
      setData(r.data);
    }).catch(() => setData({ points: [] }));
  }, [days, autoFell]);

  const points = withLive(data?.points || [], data && live);
  const change = seriesChange(points, "woodrow") + seriesChange(points, "portfolio");
  const showPortfolio = points.filter(p => p.portfolio != null).length >= 2;
  const totals = points.map(p => (p.woodrow || 0) + (showPortfolio ? (p.portfolio || 0) : 0));
  const lo = Math.min(...totals), hi = Math.max(...totals);
  const pad = Math.max((hi - lo) * 0.15, hi * 0.0005, 1);
  const domain = [Math.floor(lo - pad), Math.ceil(hi + pad)];
  const lineColor = change >= 0 ? "rgb(var(--bt-positive))" : "rgb(var(--bt-negative))";

  return (
    <div className="mt-5" data-testid="value-trend-chart">
      <div className="flex items-center justify-between mb-2">
        <p className="font-mono text-[10px] uppercase tracking-wider text-bt-text-tertiary">
          Value trend{points.length > 1 && (
            <span className={`ml-2 normal-case ${change >= 0 ? "text-bt-positive" : "text-bt-negative"}`} data-testid="value-trend-change">
              {change >= 0 ? "+" : "-"}{usd(Math.abs(change))}
            </span>
          )}
        </p>
        <div className="flex gap-1">
          {RANGES.map(r => (
            <button
              key={r.label}
              onClick={() => setDays(r.days)}
              className={`px-2 py-0.5 font-mono text-[10px] border transition-colors ${days === r.days ? "border-bt-gold text-bt-gold bg-bt-gold/10" : "border-bt-border-subtle text-bt-text-tertiary hover:text-bt-text-primary"}`}
              data-testid={`value-trend-range-${r.label.toLowerCase()}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>
      <div className="h-32">
        {points.length < 2 ? (
          <p className="h-full flex items-center justify-center font-mono text-[11px] text-bt-text-tertiary" data-testid="value-trend-empty">
            {data ? "Not enough history yet — check back tomorrow" : "Loading…"}
          </p>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={points} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
              <XAxis dataKey="date" hide />
              <YAxis domain={domain} allowDataOverflow orientation="right" width={60} tickCount={3}
                tick={{ fontSize: 9, fontFamily: "monospace", fill: "rgb(var(--bt-text-tertiary))" }} axisLine={false} tickLine={false}
                tickFormatter={(v) => (hi - lo < 5000 ? usd(v) : `$${(v / 1000).toFixed(1)}k`)} />
              <Tooltip content={<TrendTooltip />} cursor={{ stroke: "rgb(var(--bt-border-focus))" }} />
              <Area type="monotone" stackId="v" dataKey="woodrow" stroke={showPortfolio ? "rgb(var(--bt-gold))" : lineColor} strokeWidth={1.5} fill="rgb(var(--bt-gold))" fillOpacity={0.15} dot={false} connectNulls />
              {showPortfolio && <Area type="monotone" stackId="v" dataKey="portfolio" stroke="rgb(var(--bt-positive))" strokeWidth={1.5} fill="rgb(var(--bt-positive))" fillOpacity={0.15} dot={false} />}
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
      {data?.portfolio_tracking_since && (
        <p className="mt-1 font-mono text-[10px] text-bt-text-tertiary" data-testid="value-trend-note">
          <span className="text-bt-gold">■</span> Woodrow · <span className="text-bt-positive">■</span> My Portfolio (tracked since {data.portfolio_tracking_since}{showPortfolio ? "" : " — appears after 2 days"})
        </p>
      )}
    </div>
  );
};
